import { PageBlueprint } from '@backstage/frontend-plugin-api';
import catalogPlugin, {
  CatalogIndexPage,
} from '@backstage/plugin-catalog/alpha';
import {
  EntityCardBlueprint,
  EntityContentLayoutBlueprint,
  entityColumnPresets,
  EntityColumnConfig,
} from '@backstage/plugin-catalog-react/alpha';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
} from '@backstage/plugin-catalog';
import { NonEmptyRelationCard } from './NonEmptyRelationCard';
import { Cell } from '@backstage/ui';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { GraphHealthProvider, HealthNameBox } from '../datadog/GraphHealth';
import { SavrAboutCard } from './SavrAboutCard';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { Link, TableColumn } from '@backstage/core-components';
import CategoryIcon from '@material-ui/icons/Category';
import GitHubIcon from '@material-ui/icons/GitHub';
import {
  applyColumnLayout,
  ColumnLayoutPersistence,
  persistedTableOptions,
} from '../tableColumnLayout';
import { LifecyclePill, Pill, TypePill } from '../../components/tablePills';

const BUSINESS_CRITICAL_LABEL = 'savr.com/business-critical';

// Services opt in by setting the label in their catalog-info.yaml:
//   metadata:
//     labels:
//       savr.com/business-critical: 'true'
const businessCriticalColumn: TableColumn<CatalogTableRow> = {
  title: 'Critical',
  align: 'center',
  width: '8%',
  customSort: ({ entity: a }, { entity: b }) =>
    (a.metadata.labels?.[BUSINESS_CRITICAL_LABEL] === 'true' ? 1 : 0) -
    (b.metadata.labels?.[BUSINESS_CRITICAL_LABEL] === 'true' ? 1 : 0),
  render: ({ entity }) =>
    entity.metadata.labels?.[BUSINESS_CRITICAL_LABEL] === 'true' ? (
      <Pill label="Critical" tone="error" dot />
    ) : null,
};

// Custom column example: link to the GitHub repo from the standard
// github.com/project-slug annotation. Add more columns here as needed -
// anything on the entity (annotations, labels, spec fields) is available
// via row.entity.
const repoColumn: TableColumn<CatalogTableRow> = {
  title: 'Repo',
  align: 'center',
  width: '7%',
  render: ({ entity }) => {
    const slug = entity.metadata.annotations?.['github.com/project-slug'];
    return slug ? (
      <Link to={`https://github.com/${slug}`} title={slug}>
        <GitHubIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
      </Link>
    ) : null;
  },
};

// Components/APIs get our column set; other kinds (Group, User, ...) keep the
// stock per-kind columns. All kinds get the shared saved column layout
// applied (see ../tableColumnLayout).
const columns: CatalogTableColumnsFunc = entityListContext => {
  const kind =
    entityListContext.filters.kind?.value?.toLocaleLowerCase('en-US') ??
    'component';
  const defaultColumns =
    kind !== 'component' && kind !== 'api'
      ? CatalogTable.defaultColumnsFunc(entityListContext)
      : [
          {
            ...CatalogTable.columns.createNameColumn({ defaultKind: kind }),
            // Keep service names on a single line; ellipsize instead of
            // overflowing into the next column when space runs out.
            cellStyle: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            width: '18%',
          },
          {
            ...CatalogTable.columns.createOwnerColumn(),
            width: '10%',
            cellStyle: { whiteSpace: 'nowrap' },
          },
          {
            ...CatalogTable.columns.createSpecTypeColumn(),
            width: '9%',
            render: ({ entity }: CatalogTableRow) => (
              <TypePill type={entity.spec?.type} />
            ),
          },
          {
            ...CatalogTable.columns.createSpecLifecycleColumn(),
            width: '9%',
            render: ({ entity }: CatalogTableRow) => (
              <LifecyclePill lifecycle={entity.spec?.lifecycle} />
            ),
          },
          businessCriticalColumn,
          repoColumn,
          {
            ...CatalogTable.columns.createMetadataDescriptionColumn(),
            width: '22%',
          },
          { ...CatalogTable.columns.createTagsColumn(), width: '17%' },
        ];
  return applyColumnLayout(defaultColumns);
};

// Replaces the stock page:catalog extension (the default one takes no columns
// prop, so a page override is the supported way to customize the table).
const catalogIndexPage = PageBlueprint.make({
  params: {
    path: '/catalog',
    routeRef: catalogPlugin.routes.catalogIndex,
    icon: <CategoryIcon fontSize="inherit" />,
    title: 'Catalog',
    loader: async () => (
      <ColumnLayoutPersistence>
        <CatalogIndexPage
          columns={columns}
          pagination
          tableOptions={persistedTableOptions}
        />
      </ColumnLayoutPersistence>
    ),
  },
});

// Replaces the default overview layout with one that hides the warning
// banners (orphan/relation/processing errors) from external users - the
// relation warning enumerates entity refs externals are not allowed to see.
// Custom layouts registered on the overview content win over the default.
const savrEntityContentLayout = EntityContentLayoutBlueprint.make({
  name: 'savr',
  params: {
    loader: () =>
      import('./SavrEntityContentLayout').then(m => m.SavrEntityContentLayout),
  },
});

// Replaces the stock About card (same 'about' extension id) so external users
// without docs access don't get an active "View TechDocs" link.
const savrAboutCard = EntityCardBlueprint.make({
  name: 'about',
  params: {
    type: 'info',
    loader: async () => <SavrAboutCard />,
  },
});

// Live health in the relation tables (Depends on components, Has components,
// Has subcomponents): the name of an entity with ping checks gets a green/red
// box, fed by the same health summary as the catalog graph. Entities without
// checks render plain.
const [presetNameColumn, ...presetRestColumns] =
  entityColumnPresets.component.columns;
const healthNameColumn: EntityColumnConfig = {
  ...presetNameColumn,
  cell: item => (
    <Cell>
      <HealthNameBox entityRef={stringifyEntityRef(item)}>
        <EntityRefLink
          entityRef={item}
          defaultKind="component"
          title={item.metadata?.title}
        />
      </HealthNameBox>
    </Cell>
  ),
};
const columnsWithHealth = [healthNameColumn, ...presetRestColumns];

// Replace the stock relation cards (same extension ids) with health-aware
// tables that render only when the entity actually has such relations -
// stacks of empty "No X. Learn how to change this." cards are just noise.
const savrDependsOnComponentsCard = EntityCardBlueprint.make({
  name: 'depends-on-components',
  params: {
    filter: { kind: 'component' },
    loader: async () => (
      <NonEmptyRelationCard type="dependsOn" targetKind="component">
        <GraphHealthProvider>
          <EntityDependsOnComponentsCard columnConfig={columnsWithHealth} />
        </GraphHealthProvider>
      </NonEmptyRelationCard>
    ),
  },
});
const savrDependsOnResourcesCard = EntityCardBlueprint.make({
  name: 'depends-on-resources',
  params: {
    filter: { kind: 'component' },
    loader: async () => (
      <NonEmptyRelationCard type="dependsOn" targetKind="resource">
        <EntityDependsOnResourcesCard />
      </NonEmptyRelationCard>
    ),
  },
});
const savrHasComponentsCard = EntityCardBlueprint.make({
  name: 'has-components',
  params: {
    filter: { kind: 'system' },
    loader: async () => (
      <NonEmptyRelationCard type="hasPart" targetKind="component">
        <GraphHealthProvider>
          <EntityHasComponentsCard columnConfig={columnsWithHealth} />
        </GraphHealthProvider>
      </NonEmptyRelationCard>
    ),
  },
});
const savrHasResourcesCard = EntityCardBlueprint.make({
  name: 'has-resources',
  params: {
    filter: { kind: 'system' },
    loader: async () => (
      <NonEmptyRelationCard type="hasPart" targetKind="resource">
        <EntityHasResourcesCard />
      </NonEmptyRelationCard>
    ),
  },
});
const savrHasSubcomponentsCard = EntityCardBlueprint.make({
  name: 'has-subcomponents',
  params: {
    filter: { kind: 'component' },
    loader: async () => (
      <NonEmptyRelationCard type="hasPart" targetKind="component">
        <GraphHealthProvider>
          <EntityHasSubcomponentsCard columnConfig={columnsWithHealth} />
        </GraphHealthProvider>
      </NonEmptyRelationCard>
    ),
  },
});

export const catalogPluginWithOverrides = catalogPlugin.withOverrides({
  extensions: [
    catalogIndexPage,
    savrEntityContentLayout,
    savrAboutCard,
    savrDependsOnComponentsCard,
    savrDependsOnResourcesCard,
    savrHasComponentsCard,
    savrHasResourcesCard,
    savrHasSubcomponentsCard,
  ],
});
